/**
 * Configuration de base pour toutes les API RTK Query.
 * Centralise l'URL de l'API et les headers communs (auth + année académique).
 * Gère le refresh automatique du token JWT en cas de 401.
 */

import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Récupère l'année académique depuis le localStorage.
 */
export const getAnneeAcademique = (): string | null => {
  return localStorage.getItem('annee_academique');
};

/**
 * Récupère l'ID de l'année académique depuis le localStorage.
 */
export const getAnneeAcademiqueId = (): string | null => {
  return localStorage.getItem('annee_academique_id');
};

/**
 * Définit l'année académique dans le localStorage.
 */
export const setAnneeAcademique = (libelle: string, id: number): void => {
  localStorage.setItem('annee_academique', libelle);
  localStorage.setItem('annee_academique_id', String(id));
};

/**
 * Supprime l'année académique du localStorage.
 */
export const clearAnneeAcademique = (): void => {
  localStorage.removeItem('annee_academique');
  localStorage.removeItem('annee_academique_id');
};

/** Page du portail étudiant PWA (`/portail/*`), à ne pas confondre avec `/portail-admin`. */
export const estPagePortailEtudiant = (path: string): boolean =>
  path === '/portail' || path.startsWith('/portail/');

/**
 * Base query interne avec headers d'authentification et d'année académique.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers) => {
    // Sur les pages du portail étudiant, priorité au portail_access_token
    // (le user peut avoir un auth_token backoffice obsolète en localStorage
    // d'une session précédente qui provoquerait des 401 côté messagerie-chat).
    // Partout ailleurs, priorité au JWT admin.
    // Fix 2026-09-11 : `/portail` ou `/portail/…` seulement. `startsWith('/portail')`
    // attrapait aussi `/portail-admin` : un agent qui avait ouvert le portail
    // étudiant dans le même navigateur envoyait le jeton étudiant aux endpoints
    // d'administration et la page « Portail étudiant » restait vide.
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const isPortail = estPagePortailEtudiant(path);
    const portailToken = localStorage.getItem('portail_access_token');
    const adminToken = localStorage.getItem('auth_token')
      || localStorage.getItem('access_token');
    const token = isPortail
      ? (portailToken || adminToken)
      : (adminToken || portailToken);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    const anneeAcademique = getAnneeAcademique();
    if (anneeAcademique) {
      headers.set('X-Annee-Academique', anneeAcademique);
    }

    return headers;
  },
});

/**
 * Base query avec refresh automatique du token JWT.
 * En cas de 401, tente de rafraîchir le token via /api/auth/token/refresh/.
 * Si le refresh échoue, redirige vers la page de login.
 */
export const baseQueryWithHeaders: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Tenter le refresh
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      const refreshResult = await rawBaseQuery(
        {
          url: '/auth/token/refresh/',
          method: 'POST',
          body: { refresh: refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        const data = refreshResult.data as { access: string };
        // Stocker le nouveau access token
        localStorage.setItem('auth_token', data.access);
        localStorage.setItem('access_token', data.access);

        // Relancer la requête originale avec le nouveau token
        result = await rawBaseQuery(args, api, extraOptions);
      } else {
        // Refresh échoué → déconnexion complète (tokens + user hydraté)
        // Sans supprimer user_data, authSlice.getSavedUser() ré-hydrate à
        // chaque hard-reload → composants globaux (ex. RappelTachesModal)
        // relancent leur query → 401 → reload → boucle infinie.
        localStorage.removeItem('auth_token');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        // Si on est sur le portail étudiant, ne pas rediriger vers le login
        // backoffice : c'est le composant portail qui gère son propre login.
        const path = window.location.pathname;
        if (path !== '/login' && !estPagePortailEtudiant(path)) {
          window.location.href = '/login';
        }
      }
    } else {
      // Pas de refresh token → nettoyage complet + redirection
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      // Idem : ne pas rediriger vers /login backoffice depuis le portail.
      const path = window.location.pathname;
      if (path !== '/login' && !estPagePortailEtudiant(path)) {
        window.location.href = '/login';
      }
    }
  }

  return result;
};

/**
 * Type pour le baseQuery avec headers
 */
export type BaseQueryWithHeaders = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>;
